import React from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { Channel, SenderType, StaffOption } from "../utils/types";
import { ArrivalChannelTabs } from "./ArrivalChannelTabs";
import { MessagingSettingsCardFooter } from "./MessagingSettingsCardFooter";
import { SenderTypePicker } from "./SenderTypePicker";
import { StaffAssignmentPanel } from "./StaffAssignmentPanel";

type ArrivalMessageCardProps = {
  channel: Channel;
  onChannelChange: (channel: Channel) => void;
  senderType: SenderType;
  onSenderTypeChange: (value: SenderType) => void;
  staffId: string;
  onStaffIdChange: (id: string) => void;
  fallback: string;
  onFallbackChange: (value: string) => void;
  staffOptions: StaffOption[];
  selectedStaff?: StaffOption;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
};

export function ArrivalMessageCard({
  channel,
  onChannelChange,
  senderType,
  onSenderTypeChange,
  staffId,
  onStaffIdChange,
  fallback,
  onFallbackChange,
  staffOptions,
  selectedStaff,
  onCancel,
  onSave,
  isSaving,
}: ArrivalMessageCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="max-w-4xl gap-0 py-0 shadow-sm">
      <CardHeader className="mb-6 border-0 px-6 pt-6 pb-0">
        <CardTitle className="text-foreground text-2xl font-semibold tracking-tight">
          {t("layout.messagingSettingsPage.tabs.arrivalMessage")}
        </CardTitle>
        <CardDescription>
          {t("layout.messagingSettingsPage.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 px-6 pt-0 pb-2">
        <ArrivalChannelTabs
          channel={channel}
          onChannelChange={onChannelChange}
        />
        <SenderTypePicker
          senderType={senderType}
          onSenderTypeChange={onSenderTypeChange}
        />
        {senderType === "staff" && (
          <StaffAssignmentPanel
            staffId={staffId}
            onStaffIdChange={onStaffIdChange}
            fallback={fallback}
            onFallbackChange={onFallbackChange}
            staffOptions={staffOptions}
            selectedStaff={selectedStaff}
          />
        )}
      </CardContent>
      <MessagingSettingsCardFooter
        onCancel={onCancel}
        onSave={onSave}
        isSaving={isSaving}
      />
    </Card>
  );
}
